import {Action} from 'com/gongxigames/math/core/game/enums/Action';
import {Message} from 'com/gongxigames/math/core/game/message/Message';
import {RubyPlayGameClientWrapper} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapper';
import {PSLaunchError, PSLaunchResponse, XCLaunchOptions} from '@gongxigames/framework/integrations/pokerstars/PSTypes';
import {XCClientAdapter} from '@gongxigames/framework/integrations/pokerstars/XCClientAdapter';
import {RubyPlayErrorsHandler} from '@gongxigames/framework/integrations/rubyplay/RubyPlayErrorsHandler';
import {PSErrorsHandler} from '@gongxigames/framework/integrations/pokerstars/PSErrorsHandler';
import {consoleLogger, DeviceType, IEventManagerWithAsync, LanguageCodeList} from '@gongxigames/framework/core';
import {ResponseBody, ServerResponseData} from '@gongxigames/framework/integrations/rubyplay/types';
import {PS_SETTINGS_PARAMETERS_SCHEMA, PSSettings} from '@gongxigames/framework/integrations/pokerstars/PSSettings';
import {PSGameClientWrapperEvents} from '@gongxigames/framework/integrations/pokerstars/PSGameClientWrapperEvents';
import {
    CancelStartSpinData,
    CancelStartSpinReason,
    GameClientWrapperSettings,
    SlotSettings,
    Xhr
} from '@gongxigames/framework/slot';
import {strictUiEmitter} from '@gongxigames/framework/slotV2/globals';

const PING_BALANCE_DELAY = 15000;

export class PokerStartsGameClientWrapper extends RubyPlayGameClientWrapper {
    public static NAME = 'pokerstars';
    private static launchResponseBody?: PSLaunchResponse;
    private launchURL!: string;
    private xcClientAdapter!: XCClientAdapter;
    private xcLaunchOptions!: XCLaunchOptions;
    private isWaitRequest = false;

    protected createErrorsHandler<E extends RubyPlayErrorsHandler>(): E | RubyPlayErrorsHandler {
        return new PSErrorsHandler(this._eventManager);
    }

    public init(): void {
        this.setSpecificSettings();

        Promise.resolve()
            .then(this.launch.bind(this))
            .then(() => super.init())
            .catch(() => {
                consoleLogger.error('PokerStartsGameClientWrapper not initialized!');
            });
    }

    protected onInitResponse(data: ServerResponseData): void {
        super.onInitResponse(data);

        this.xcClientAdapter.isRestoredGame = this.isNextActionNotSpin();
    }

    protected initParameters(): void {
        super.initParameters();

        const settings = this.getSettings<PSSettings>();

        settings.addParametersSchema(PS_SETTINGS_PARAMETERS_SCHEMA);

        const xcLaunchGameOptions = settings.get('xcLaunchGameOptions');

        if (xcLaunchGameOptions) {
            // for xc-game-loadingScreen in iOS native app and white-label
            this.xcLaunchOptions = xcLaunchGameOptions;
        } else {
            this.xcLaunchOptions = (window.RP_CONFIG!.keysAndValues as XCLaunchOptions);
        }

        this.xcLaunchOptions.siteName = settings.get('siteName');
        this.xcClientAdapter = new XCClientAdapter();
        this.xcClientAdapter.init(this.xcLaunchOptions);

        const {vendorGameConfig, languageCode, currency = 'USD', soundEnabled, rgToken} = this.xcLaunchOptions;
        const {gameCode} = JSON.parse(vendorGameConfig);
        const lang = LanguageCodeList[languageCode as LanguageCodeList] || 'en';
        const serverUrl = settings.get('serverUrl');

        settings.set('lang', lang);
        settings.set('currency', currency);
        settings.set('soundsMuted', !soundEnabled);
        settings.set('currency', currency);
        settings.set('gameName', gameCode);
        this.currencyFormatter.currencyCode = currency;

        this._initSessionUrl = `${serverUrl}/init-session`;
        this._gameServerUrl = `${serverUrl}/gameserver`;
        this.launchURL = `${serverUrl}/aux/launcher`;
        this._gameName = gameCode;
        this._playerSession = rgToken;

        this._initSessionUrl = this._modeHandler.extendUrl(this._initSessionUrl);
        this._gameServerUrl = this._modeHandler.extendUrl(this._gameServerUrl);
    }

    protected launch(): Promise<void> {
        return this.launchRequest();
    }

    protected setupEvents(): void {
        this.on('General.Window.RELOAD', this.reload);
        this.on('General.Game.SYSTEM_INITIALIZED', this.onSystemInitialized);

        const eventManager = (this._eventManager as IEventManagerWithAsync<PSGameClientWrapperEvents>);

        strictUiEmitter.on('General.Flow.cancelStartSpin', this.onCancelStartSpin.bind(this));
        eventManager.on('General.Flow.cancelStartSpin', this.onCancelStartSpin.bind(this), this);
        eventManager.on('XCAPIEvents.balanceUpdated', this.forcePingBalance.bind(this), this);
    }

    protected launchRequest(): Promise<void> {
        if (this.getSettings<SlotSettings>().get('offlineMode')) {
            return Promise.resolve();
        }

        const data = {
            userId          : this.xcLaunchOptions.userId,
            rgToken         : this.xcLaunchOptions.rgToken,
            newSession      : this.xcLaunchOptions.isNewSession,
            currency        : this.xcLaunchOptions.currency,
            siteId          : this.xcLaunchOptions.siteId,
            vendorGameConfig : this.xcLaunchOptions.vendorGameConfig,
            countryCode     : this.xcLaunchOptions.countryCode,
            languageCode    : this.xcLaunchOptions.languageCode,
            platform        : this.xcLaunchOptions.platform,
            hostId          : this.xcLaunchOptions.hostId,
            siteName        : this.xcLaunchOptions.siteName
        };

        if (PokerStartsGameClientWrapper.launchResponseBody) {
            this.launchResponse(PokerStartsGameClientWrapper.launchResponseBody);

            return Promise.resolve();
        }

        return this.postLaunchRequest(data);
    }

    protected gameServerActionRequest(request: Message): void {
        super.gameServerActionRequest(request);

        this.isWaitRequest = true;
    }

    protected onGameServerActionResponse(body: ResponseBody, action: Action): void {
        super.onGameServerActionResponse(body, action);

        this.isWaitRequest = false;
    }

    private forcePingBalance(): void {
        if (!this.isWaitRequest) {
            this.pingBalance();
        }
    }

    private postLaunchRequest(data: any): Promise<void> {
        return new Promise<void>((res, reject) => {
            Xhr.post(this.launchURL, JSON.stringify(data), (body: PSLaunchResponse | PSLaunchError) => {
                if ((body as PSLaunchResponse).playForFun !== undefined) {

                    PokerStartsGameClientWrapper.launchResponseBody = body as PSLaunchResponse;

                    this.launchResponse(body as PSLaunchResponse);
                    res();
                } else {
                    this.getErrorsHandler<PSErrorsHandler>().handleLaunchError(body as PSLaunchError);
                }
            }, {'Content-Type': 'application/json'}, (xhr: any) => {
                this.onErrorRequest(xhr);
                reject();
            });
        });
    }

    protected launchResponse({operator}: PSLaunchResponse): void {
        this._operator = operator;
    }

    protected isNextActionNotSpin(): boolean {
        return this.getSession().nextAction() !== Action.SPIN;
    }

    protected onCancelStartSpin({reason}: CancelStartSpinData): void {
        if (reason === CancelStartSpinReason.INSUFFICIENT_FUNDS) {
            this.getErrorsHandler<PSErrorsHandler>().handleGameClientInsufficientFundsError();
        }
    }

    private setSpecificSettings(): void {
        const settings = this.getSettings<SlotSettings>();

        settings.set('hideHomeButton', true);
        settings.set('hideCashierButton', true);
        settings.set('clock', false);

        if (this._deviceType === DeviceType.DESKTOP) {
            settings.set('hideSoundCheckBox', true);
            settings.set('showBalance', false);
        }
    }

    protected reload(): void {
        this._eventManager.emit('General.Game.SOFT_RELOAD');
    }

    protected getPingBalanceDelay(): number {
        return PING_BALANCE_DELAY;
    }

    public getDefaultBet(): number {
        if (this.getSettings<SlotSettings>().get('offlineMode')) {
            return this.getDefaultBetForOfflineMode();
        }

        return super.getDefaultBet();
    }

    private getDefaultBetForOfflineMode(): number {
        const bets = this.getBets();
        const defaultStake = this.getSettings<GameClientWrapperSettings>().get('defaultStake');

        if (defaultStake && bets.indexOf(defaultStake * this.currencyFormatter.fractionSize) > -1) {
            return defaultStake * this.currencyFormatter.fractionSize;
        }

        return bets[0];
    }
}
